import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { format, addMonths, isSaturday, isSunday, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';
import axios from '../../api/axios';

// Supports new {cantidad, unidad} entries and legacy metodo strings like '1_mes' / '14_dias'
const parseMetodo = (entry) => {
  if ('unidad' in entry || 'cantidad' in entry) return { cantidad: entry.cantidad ?? '', unidad: entry.unidad ?? '' };
  if (entry.metodo === 'manual') return { cantidad: '', unidad: 'manual' };
  const m = /^(\d+)_(dias?|mes|meses)$/.exec(entry.metodo || '');
  if (!m) return { cantidad: '', unidad: '' };
  return { cantidad: m[1], unidad: m[2].startsWith('dia') ? 'dias' : 'meses' };
};

const MetodoInputs = ({ numero, value, onCantidadChange, onApply }) => (
  <div className="flex gap-2">
    {value.unidad !== 'manual' && (
      <Input
        type="number"
        min="1"
        className="h-9 text-xs w-20"
        placeholder="Nº"
        value={value.cantidad}
        onChange={(e) => onCantidadChange(e.target.value)}
        onBlur={(e) => onApply(e.target.value, value.unidad)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onApply(e.target.value, value.unidad);
          }
        }}
        data-testid={`salida-camara-cantidad-${numero}`}
      />
    )}
    <Select value={value.unidad || ''} onValueChange={(u) => onApply(value.cantidad, u)}>
      <SelectTrigger className="h-9 text-xs flex-1" data-testid={`salida-camara-unidad-${numero}`}>
        <SelectValue placeholder="Unidad..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="dias">Días</SelectItem>
        <SelectItem value="meses">Meses</SelectItem>
        <SelectItem value="manual">Calendario</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const DateField = ({ label, value, onChange, testId }) => {
  const parsed = /^\d{2}\/\d{2}\/\d{4}$/.test(value || '')
    ? new Date(value.slice(6), value.slice(3, 5) - 1, value.slice(0, 2)) : undefined;
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-gray-700">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid={testId}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value || <span className="text-gray-400">Seleccionar fecha</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={parsed} defaultMonth={parsed} locale={es}
            onSelect={(date) => onChange(date ? format(date, 'dd/MM/yyyy') : '')} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const SalidaCamaraSection = ({
  entries, onChange, fechaEntradaGoya, externos, fechaEnvioExternos, fechaRecepcionExternos, onFieldChange,
  fechaInformeEstabilidades, onFechaInformeChange,
}) => {
  const [weekendDialog, setWeekendDialog] = useState({
    open: false,
    entryIndex: null,
    calculatedDate: null,
    proposedDate: null,
    dayName: '',
  });
  const [manualOverrideDate, setManualOverrideDate] = useState(null);
  const [festivos, setFestivos] = useState({});

  useEffect(() => {
    axios.get('/festivos').then((r) => setFestivos(Object.fromEntries(r.data.map((f) => [f.fecha, f.nombre]))))
      .catch(() => toast.error('No se pudieron cargar los festivos; el cálculo solo saltará fines de semana'));
  }, []);

  const festivoDe = (d) => festivos[format(d, 'yyyy-MM-dd')];
  const isWorking = (d) => !isSaturday(d) && !isSunday(d) && !festivoDe(d);
  const shiftToWorking = (d, step) => {
    let r = d;
    while (!isWorking(r)) r = addDays(r, step);
    return r;
  };
  const addWorkingDays = (d, n) => {
    let r = d;
    for (let i = 0; i < n; i++) r = shiftToWorking(addDays(r, 1), 1);
    return r;
  };
  // Saturday/holiday -> previous working day; Sunday -> next working day
  const proposeWorking = (d) => (isSunday(d) ? shiftToWorking(d, 1) : shiftToWorking(d, -1));
  const nonWorkingName = (d) => (isSaturday(d) ? 'sábado' : isSunday(d) ? 'domingo' : `festivo (${festivoDe(d)})`);

  const parseFechaEntrada = () => {
    if (!fechaEntradaGoya) return null;
    try {
      if (fechaEntradaGoya.includes('/')) {
        const [d, m, y] = fechaEntradaGoya.split('/');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      return new Date(fechaEntradaGoya);
    } catch {
      return null;
    }
  };

  const addEntry = () => {
    const newNum = entries.length > 0 ? Math.max(...entries.map(e => e.numero)) + 1 : 1;
    onChange([...entries, { numero: newNum, fecha: '', metodo: '', cantidad: '', unidad: '' }]);
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) return;
    const updated = entries.filter((_, i) => i !== index)
      .map((e, i) => ({ ...e, numero: i + 1 }));
    onChange(updated);
  };

  const setEntry = (index, patch) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], ...patch };
    onChange(updated);
  };

  const applyMetodo = (index, cantidad, unidad) => {
    if (unidad === 'manual') {
      setEntry(index, { cantidad: '', unidad, metodo: 'manual', fecha: '' });
      return;
    }
    const n = parseInt(cantidad, 10);
    const metodo = n > 0 && unidad ? `${n}_${unidad}` : '';
    const baseDate = parseFechaEntrada();
    if (!metodo || !baseDate) {
      setEntry(index, { cantidad, unidad, metodo, fecha: '' });
      return;
    }
    if (entries[index].metodo === metodo && entries[index].fecha && entries[index].base === fechaEntradaGoya) return;

    const calculated = unidad === 'meses' ? addMonths(baseDate, n) : addDays(baseDate, n);

    if (!isWorking(calculated)) {
      const proposed = proposeWorking(calculated);
      setEntry(index, { cantidad, unidad });
      setManualOverrideDate(proposed);
      setWeekendDialog({
        open: true,
        entryIndex: index,
        calculatedDate: calculated,
        proposedDate: proposed,
        dayName: nonWorkingName(calculated),
        metodo,
        cantidad,
        unidad,
      });
    } else {
      setEntry(index, { cantidad, unidad, metodo, base: fechaEntradaGoya, fecha: format(calculated, 'dd/MM/yyyy') });
    }
  };

  // Recalculate automatic dates when F. Entrada GOYA changes (weekend -> proposed Friday/Monday)
  const prevEntrada = useRef(fechaEntradaGoya);
  useEffect(() => {
    if (prevEntrada.current === fechaEntradaGoya) return;
    prevEntrada.current = fechaEntradaGoya;
    const baseDate = parseFechaEntrada();
    if (!baseDate) return;
    let moved = 0;
    const updated = entries.map((e) => {
      const { cantidad, unidad } = parseMetodo(e);
      const n = parseInt(cantidad, 10);
      if (unidad === 'manual' || !unidad || !(n > 0)) return e;
      let d = unidad === 'meses' ? addMonths(baseDate, n) : addDays(baseDate, n);
      if (!isWorking(d)) { d = proposeWorking(d); moved++; }
      return { ...e, cantidad, unidad, metodo: `${n}_${unidad}`, base: fechaEntradaGoya, fecha: format(d, 'dd/MM/yyyy') };
    });
    if (updated.some((e, i) => e !== entries[i])) {
      onChange(updated);
      toast.info(`Fechas de salida recalculadas${moved ? ` (${moved} movida(s) por caer en fin de semana o festivo)` : ''}`);
    }
  }, [fechaEntradaGoya]);

  // F. Informe = last salida + 10 business days (20 if Externos); only when salidas/Externos change
  const lastSalida = entries
    .map((e) => (/^\d{2}\/\d{2}\/\d{4}$/.test(e.fecha || '') ? new Date(e.fecha.slice(6), e.fecha.slice(3, 5) - 1, e.fecha.slice(0, 2)) : null))
    .filter(Boolean).sort((a, b) => b - a)[0];
  const informeKey = `${lastSalida ? format(lastSalida, 'dd/MM/yyyy') : ''}|${externos}`;
  const prevInformeKey = useRef(informeKey);
  useEffect(() => {
    if (prevInformeKey.current === informeKey) return;
    prevInformeKey.current = informeKey;
    if (!lastSalida) return;
    onFechaInformeChange(format(addWorkingDays(lastSalida, externos ? 20 : 10), 'dd/MM/yyyy'));
  }, [informeKey]);

  // F. Envío Externos = next working day after F. Entrada GOYA (only when entrada/Externos change)
  const envioKey = `${fechaEntradaGoya || ''}|${externos}`;
  const prevEnvioKey = useRef(envioKey);
  useEffect(() => {
    if (prevEnvioKey.current === envioKey) return;
    prevEnvioKey.current = envioKey;
    if (!externos) {
      onFieldChange('fecha_envio_externos', '');
      onFieldChange('fecha_recepcion_externos', '');
      return;
    }
    const base = parseFechaEntrada();
    if (base) onFieldChange('fecha_envio_externos', format(addWorkingDays(base, 1), 'dd/MM/yyyy'));
  }, [envioKey]);

  // F. Recepción Informe Externos = F. Envío + 10 working days (only when envío changes)
  const prevEnvio = useRef(fechaEnvioExternos);
  useEffect(() => {
    if (prevEnvio.current === fechaEnvioExternos) return;
    prevEnvio.current = fechaEnvioExternos;
    if (!externos || !/^\d{2}\/\d{2}\/\d{4}$/.test(fechaEnvioExternos || '')) return;
    const f = fechaEnvioExternos;
    const envio = new Date(f.slice(6), f.slice(3, 5) - 1, f.slice(0, 2));
    onFieldChange('fecha_recepcion_externos', format(addWorkingDays(envio, 10), 'dd/MM/yyyy'));
  }, [fechaEnvioExternos]);

  const handleWeekendAccept = () => {
    const { entryIndex, metodo, cantidad, unidad } = weekendDialog;
    setEntry(entryIndex, { metodo, cantidad, unidad, base: fechaEntradaGoya, fecha: format(manualOverrideDate, 'dd/MM/yyyy') });
    setWeekendDialog({ ...weekendDialog, open: false });
  };

  const handleManualDateSelect = (index, date) => {
    setEntry(index, { fecha: date ? format(date, 'dd/MM/yyyy') : '', metodo: 'manual', unidad: 'manual', cantidad: '' });
  };

  const parseEntryDate = (fechaStr) => {
    if (!fechaStr) return null;
    try {
      if (fechaStr.includes('/')) {
        const [d, m, y] = fechaStr.split('/');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      return new Date(fechaStr);
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-700">Fechas Salida Cámara</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addEntry}
          className="text-xs"
          data-testid="add-salida-camara-btn"
        >
          <Plus className="h-3 w-3 mr-1" /> Añadir
        </Button>
      </div>

      {entries.map((entry, index) => {
        const metodo = parseMetodo(entry);
        return (
          <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border" data-testid={`salida-camara-${entry.numero}`}>
            <div className="flex-shrink-0 mt-1">
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                {entry.numero}
              </span>
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Método</Label>
                <MetodoInputs
                  numero={entry.numero}
                  value={metodo}
                  onCantidadChange={(cantidad) => setEntry(index, { cantidad, unidad: metodo.unidad })}
                  onApply={(cantidad, unidad) => applyMetodo(index, cantidad, unidad)}
                />
              </div>

              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Fecha</Label>
                {metodo.unidad === 'manual' ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-9 justify-start text-left text-xs font-normal" data-testid={`salida-camara-fecha-${entry.numero}`}>
                        <CalendarIcon className="mr-2 h-3 w-3" />
                        {entry.fecha || <span className="text-gray-400">Seleccionar</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={parseEntryDate(entry.fecha)}
                        defaultMonth={parseEntryDate(entry.fecha) || undefined}
                        onSelect={(date) => handleManualDateSelect(index, date)}
                        locale={es}
                      />
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="h-9 flex items-center px-3 border rounded-md bg-white text-xs" data-testid={`salida-camara-fecha-${entry.numero}`}>
                    {entry.fecha ? (
                      <span className="font-medium">{entry.fecha}</span>
                    ) : (
                      <span className="text-gray-400">
                        {!parseFechaEntrada() ? 'Indica F. Entrada GOYA' : 'Indica número y unidad'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {entries.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeEntry(index)}
                className="text-red-500 hover:text-red-700 h-9 w-9 p-0 mt-5 flex-shrink-0"
                data-testid={`remove-salida-camara-${entry.numero}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      })}

      {externos && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t" data-testid="externos-fechas">
          <DateField label="F. Envío Externos" value={fechaEnvioExternos}
            onChange={(v) => onFieldChange('fecha_envio_externos', v)} testId="fecha-envio-externos-button" />
          <DateField label="F. Recepción Informe Externos" value={fechaRecepcionExternos}
            onChange={(v) => onFieldChange('fecha_recepcion_externos', v)} testId="fecha-recepcion-externos-button" />
        </div>
      )}

      {/* F. Informe Estabilidades */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-sm font-semibold text-gray-700">F. Informe</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal"
              data-testid="fecha-informe-estabilidades-button"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {fechaInformeEstabilidades ? (
                fechaInformeEstabilidades
              ) : (
                <span className="text-gray-400">Seleccionar fecha</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={parseEntryDate(fechaInformeEstabilidades)}
              defaultMonth={parseEntryDate(fechaInformeEstabilidades) || undefined}
              onSelect={(date) => onFechaInformeChange(date ? format(date, 'dd/MM/yyyy') : '')}
              locale={es}
            />
            {fechaInformeEstabilidades && (
              <div className="px-3 pb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                  onClick={() => onFechaInformeChange('')}
                >
                  Borrar fecha
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Weekend Dialog */}
      <Dialog open={weekendDialog.open} onOpenChange={(open) => { if (!open) setWeekendDialog({ ...weekendDialog, open: false }); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Fecha cae en día no hábil</DialogTitle>
            <DialogDescription>
              La fecha calculada ({weekendDialog.calculatedDate ? format(weekendDialog.calculatedDate, 'dd/MM/yyyy (EEEE)', { locale: es }) : ''})
              cae en {weekendDialog.dayName}.
              Se propone {weekendDialog.proposedDate ? format(weekendDialog.proposedDate, 'dd/MM/yyyy (EEEE)', { locale: es }) : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Label className="text-sm">Puedes aceptar la propuesta o seleccionar otra fecha:</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {manualOverrideDate ? format(manualOverrideDate, 'dd/MM/yyyy (EEEE)', { locale: es }) : 'Seleccionar'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={manualOverrideDate}
                  onSelect={(date) => date && setManualOverrideDate(date)}
                  locale={es}
                />
              </PopoverContent>
            </Popover>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWeekendDialog({ ...weekendDialog, open: false })}>
              Cancelar
            </Button>
            <Button onClick={handleWeekendAccept} data-testid="weekend-accept-btn">
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalidaCamaraSection;
